import React, { useState, useEffect, useContext } from "react";
import {
  Mail,
  Clock,
  User,
  MessageSquare,
  ArrowLeft,
  Phone,
  Calendar,
  CheckCircle,
  AlertCircle,
  Loader,
  Globe,
  ExternalLink,
} from "lucide-react";
import { useParams, Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { AuthContext } from "../../../Context/AuthContextProvider";
import { get, put } from "../../../utils/api";

const Inquiries = () => {
  const { submissionId } = useParams();
  const { loginUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState(null);
  const [inquiries, setInquiries] = useState([]);

  useEffect(() => {
    if (loginUser) {
      fetchSubmission();
    }
  }, [submissionId, loginUser]);

  const fetchSubmission = async () => {
    setLoading(true);
    try {
      const endpoint = loginUser._id
        ? `/domain/mysubmissions/${loginUser._id}`
        : `/domain/mysubmissions?ownerName=${loginUser.username}`;

      const response = await get(endpoint);

      if (response.success) {
        const foundSubmission = response.submissions.find(
          (sub) => sub._id === submissionId
        );

        if (foundSubmission) {
          setSubmission(foundSubmission);
          setInquiries(foundSubmission.inquiries || []);
        } else {
          toast.error("Submission not found");
        }
      }
    } catch (error) {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkContacted = async (inquiryId, newStatus) => {
    try {
      const response = await put(
        `/domain/submission/${submissionId}/inquiry/${inquiryId}/status`,
        { status: newStatus }
      );

      if (response.success) {
        toast.success(`Marked as ${newStatus}`);
        fetchSubmission();
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffTime / (1000 * 60));
        return diffMinutes === 0 ? "Just now" : `${diffMinutes}m ago`;
      }
      return `${diffHours}h ago`;
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        border: "border-yellow-300",
        icon: <Clock className="w-4 h-4" />,
        label: "Pending",
      },
      responded: {
        bg: "bg-blue-100",
        text: "text-blue-800",
        border: "border-blue-300",
        icon: <CheckCircle className="w-4 h-4" />,
        label: "Responded",
      },
      closed: {
        bg: "bg-gray-100",
        text: "text-gray-800",
        border: "border-gray-300",
        icon: <CheckCircle className="w-4 h-4" />,
        label: "Closed",
      },
    };

    const badge = badges[status] || badges.pending;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${badge.bg} ${badge.text} ${badge.border}`}
      >
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-24 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-[#d4af37] animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading inquiries...</p>
        </div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-24">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center border-2 border-gray-200">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Submission Not Found
            </h3>
            <p className="text-gray-600 mb-6">
              We couldn't find the submission you're looking for.
            </p>
            <button
              onClick={() => navigate("/Dashboard/MySubmissions")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Submissions
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-24">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header with Back Button */}
        <button
          onClick={() => navigate("/Dashboard/MySubmissions")}
          className="mb-6 inline-flex items-center gap-2 text-gray-600 hover:text-[#101c4c] font-medium transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Submissions
        </button>

        {/* Domain Info Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border-2 border-[#d4af37]">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <div className="p-3 rounded-xl bg-gradient-to-br from-[#b99653] to-[#d4af37]">
                  <Globe className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-[#101c4c]">
                    {submission.name}
                  </h1>
                  <p className="text-gray-600 mt-1">Inquiries & Messages</p>
                </div>
              </div>

              <div className="flex items-center gap-6 flex-wrap mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-[#101c4c]">
                    ₹{submission.price?.toLocaleString("en-IN")}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Mail className="w-5 h-5 text-blue-500" />
                  <span className="font-semibold">
                    {inquiries.length}{" "}
                    {inquiries.length === 1 ? "Inquiry" : "Inquiries"}
                  </span>
                </div>
              </div>

              {submission.description && (
                <p className="text-gray-600 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  {submission.description}
                </p>
              )}
            </div>

            {/* Domain Action Button */}
            <Link
              to={`/Marketplace_Product/${submission._id}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              View Listing
              <ExternalLink className="w-5 h-5" />
            </Link>
          </div>
        </div>

        {/* Inquiries Section */}
        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200">
          <div className="p-6 border-b-2 border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <MessageSquare className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#101c4c]">
                  All Inquiries
                </h2>
                <p className="text-gray-600 text-sm">
                  Messages from interested buyers
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {inquiries.length === 0 ? (
              <div className="text-center py-12">
                <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  No Inquiries Yet
                </h3>
                <p className="text-gray-600">
                  You haven't received any inquiries for this domain yet.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {inquiries.map((inquiry, index) => (
                  <div
                    key={inquiry._id || index}
                    className="border-2 border-gray-200 rounded-xl p-6 hover:border-[#d4af37] transition-all bg-gradient-to-br from-white to-gray-50"
                  >
                    {/* Inquiry Header */}
                    <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="bg-gradient-to-br from-[#b99653] to-[#d4af37] p-3 rounded-full">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-[#101c4c] mb-1">
                            {inquiry.userName || "Anonymous User"}
                          </h3>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(inquiry.timestamp)}</span>
                          </div>
                        </div>
                      </div>
                      {getStatusBadge(inquiry.status || "pending")}
                    </div>

                    {/* Contact Method Badge */}
                    <div className="mb-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                        {inquiry.contactMethod === "phone" ? (
                          <>
                            <Phone className="w-4 h-4" />
                            Phone Contact
                          </>
                        ) : inquiry.contactMethod === "email" ? (
                          <>
                            <Mail className="w-4 h-4" />
                            Email Contact
                          </>
                        ) : inquiry.contactMethod === "whatsapp" ? (
                          <>
                            <Phone className="w-4 h-4" />
                            WhatsApp
                          </>
                        ) : inquiry.contactMethod === "offer" ? (
                          <>
                            <MessageSquare className="w-4 h-4" />
                            Offer Submitted
                          </>
                        ) : (
                          <>
                            <MessageSquare className="w-4 h-4" />
                            Contact Form
                          </>
                        )}
                      </span>
                    </div>

                    {/* Contact Information */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200 mb-4">
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-gray-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500 font-medium mb-1">
                            Email Address
                          </p>
                          <p className="text-sm font-semibold text-[#101c4c] break-all">
                            {inquiry.userEmail || "Not provided"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Message */}
                    {inquiry.message && (
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border-l-4 border-[#d4af37]">
                        <div className="flex items-start gap-3">
                          <MessageSquare className="w-5 h-5 text-[#b99653] flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 font-semibold mb-2">
                              MESSAGE
                            </p>
                            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                              {inquiry.message}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Response (if exists) */}
                    {inquiry.response && inquiry.response.message && (
                      <div className="mt-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border-l-4 border-green-500">
                        <div className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-xs text-green-600 font-semibold mb-2">
                              YOUR RESPONSE
                            </p>
                            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap mb-2">
                              {inquiry.response.message}
                            </p>
                            {inquiry.response.timestamp && (
                              <p className="text-xs text-gray-500">
                                Sent {formatDate(inquiry.response.timestamp)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Quick Actions */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-3 flex-wrap">
                        {inquiry.userEmail ? (
                          <a
                            href={`mailto:${inquiry.userEmail}?subject=Regarding ${submission.name}&body=Hello ${inquiry.userName || 'there'},%0D%0A%0D%0AThank you for your inquiry about ${submission.name}.%0D%0A%0D%0A`}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white rounded-lg font-semibold hover:shadow-lg transition-all text-sm"
                          >
                            <Mail className="w-4 h-4" />
                            Reply via Email
                          </a>
                        ) : (
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-500 rounded-lg text-sm">
                            <Mail className="w-4 h-4" />
                            No Email Available
                          </div>
                        )}
                        
                        {inquiry.status === "pending" && (
                          <button
                            onClick={() => handleMarkContacted(inquiry._id, "responded")}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-all text-sm"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Mark as Responded
                          </button>
                        )}
                        
                        {inquiry.status === "responded" && (
                          <button
                            onClick={() => handleMarkContacted(inquiry._id, "closed")}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-semibold transition-all text-sm"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Mark as Closed
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Additional Stats */}
        {inquiries.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-white rounded-xl p-6 border-2 border-blue-200 shadow-md">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-3 rounded-xl">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-medium">
                    Total Inquiries
                  </p>
                  <p className="text-2xl font-bold text-blue-600">
                    {inquiries.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border-2 border-yellow-200 shadow-md">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-100 p-3 rounded-xl">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-medium">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {
                      inquiries.filter((i) => i.status === "pending").length
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border-2 border-green-200 shadow-md">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 p-3 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-medium">
                    Responded
                  </p>
                  <p className="text-2xl font-bold text-green-600">
                    {inquiries.filter((i) => i.status === "responded" || i.status === "closed").length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inquiries;